<template>
  <div class="wholesaler-select-page">
    <!-- Background decoration -->
    <div class="bg-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>

    <!-- Main content -->
    <div class="select-content">
      <!-- Header -->
      <div class="header-section">
        <div class="header-icon">🏪</div>
        <h1 class="page-title">{{ $t('WholesalerSelect.line15_text') }}</h1>
        <p class="page-subtitle">{{ $t('WholesalerSelect.line16_text') }}</p>
      </div>

      <!-- Wholesaler list -->
      <div class="wholesaler-list">
        <div 
          v-for="wholesaler in wholesalerOptions" 
          :key="wholesaler.wholesalerId"
          class="wholesaler-card"
          :class="{ selected: selectedWholesalerId === wholesaler.wholesalerId }"
          @click="confirmSelection(wholesaler.wholesalerId)"
        >
          <div class="card-header">
            <div class="company-icon">🏢</div>
            <div class="company-info">
              <h3 class="company-name">{{ wholesaler.companyName }}</h3>
              <p class="company-address" v-if="wholesaler.address">
                📍 {{ wholesaler.address }}
              </p>
            </div>
            <div class="radio-icon">
              <div class="radio-circle" :class="{ checked: selectedWholesalerId === wholesaler.wholesalerId }">
                <div v-if="selectedWholesalerId === wholesaler.wholesalerId" class="radio-dot"></div>
              </div>
            </div>
          </div>
          <div class="card-footer" v-if="wholesaler.contactPhone">
            <span class="phone-label">{{ $t('WholesalerSelect.line43_text') }}</span>
            <span class="phone-value">{{ wholesaler.contactPhone }}</span>
          </div>
        </div>
      </div>

      <!-- Back button -->
      <button class="btn-back" @click="goBack">
        ← 返回
      </button>
    </div>

    <!-- Toast notification -->
    <div v-if="toast.show" :class="['toast', `toast-${toast.type}`, { show: toast.show }]">
      {{ toast.message }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'WholesalerSelect',
  data() {
    return {
      wholesalerOptions: [],
      selectedWholesalerId: null,
      tempRoleCode: '',
      tempRedirectPath: '',
      toast: {
        show: false,
        message: '',
        type: 'info'
      }
    };
  },
  mounted() {
    this.loadWholesalerOptions();
  },
  methods: {
    loadWholesalerOptions() {
      try {
        // 从 sessionStorage 获取批发商列表
        const optionsStr = sessionStorage.getItem('wholesalerOptions');
        if (!optionsStr) {
          this.showToast('未获取到商户列表，请重新登录', 'error');
          setTimeout(() => {
            this.$router.replace('/login');
          }, 2000);
          return;
        }

        this.wholesalerOptions = JSON.parse(optionsStr);
        console.log('🏪 批发商列表:', this.wholesalerOptions);

        // 如果只有一个批发商，自动选中
        if (this.wholesalerOptions.length === 1) {
          this.selectedWholesalerId = this.wholesalerOptions[0].wholesalerId;
          console.log('✅ 只有一个批发商，自动选中:', this.selectedWholesalerId);
        }

        // 获取临时保存的角色信息
        this.tempRoleCode = sessionStorage.getItem('tempRoleCode') || 'WHOLESALER';
        this.tempRedirectPath = sessionStorage.getItem('tempRedirectPath') || '/wholesaler/home';
      } catch (error) {
        console.error('❌ 加载批发商列表失败:', error);
        this.showToast('加载失败，请重新登录', 'error');
      }
    },

    confirmSelection(wholesalerId) {
      if (!wholesalerId) {
        this.showToast('请选择一个批发商', 'error');
        return;
      }

      try {
        console.log('✅ 确认选择商户 ID:', wholesalerId);
        this.selectedWholesalerId = wholesalerId;

        // 保存角色和商户 ID
        localStorage.setItem('user_identity', this.tempRoleCode);
        localStorage.setItem('stored_wholesaler_id', wholesalerId.toString());

        // 清除临时数据
        sessionStorage.removeItem('tempUsername');
        sessionStorage.removeItem('tempPassword');
        sessionStorage.removeItem('tempRoleCode');
        sessionStorage.removeItem('tempRedirectPath');
        sessionStorage.removeItem('needsWholesalerSelect');
        sessionStorage.removeItem('wholesalerOptions');

        this.showToast('登录成功', 'success');

        // 直接跳转到首页
        setTimeout(() => {
          this.$router.replace({ path: this.tempRedirectPath });
        }, 300);
      } catch (error) {
        console.error('❌ 确认选择失败:', error);
        this.showToast('登录失败，请重试', 'error');
      }
    },

    goBack() {
      // 清除临时数据，返回登录页
      sessionStorage.removeItem('tempUsername');
      sessionStorage.removeItem('tempPassword');
      sessionStorage.removeItem('tempRoleCode');
      sessionStorage.removeItem('tempRedirectPath');
      sessionStorage.removeItem('needsWholesalerSelect');
      sessionStorage.removeItem('wholesalerOptions');
      this.$router.replace('/login');
    },

    showToast(message, type = 'info') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
      setTimeout(() => {
        this.toast.show = false;
      }, 2000);
    }
  }
};
</script>

<style scoped>
.wholesaler-select-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
}

.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.circle-1 {
  width: 300px;
  height: 300px;
  top: -100px;
  right: -100px;
}

.circle-2 {
  width: 200px;
  height: 200px;
  bottom: -50px;
  left: -50px;
}

.circle-3 {
  width: 150px;
  height: 150px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.select-content {
  position: relative;
  z-index: 1;
  max-width: 600px;
  margin: 0 auto;
  padding: 40px 20px;
}

.header-section {
  text-align: center;
  margin-bottom: 40px;
  color: #fff;
}

.header-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.page-title {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 12px 0;
}

.page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
}

.wholesaler-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
}

.wholesaler-card {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.wholesaler-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

.wholesaler-card.selected {
  border: 2px solid #667eea;
  background: linear-gradient(135deg, #f5f7ff 0%, #e8ecff 100%);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.company-icon {
  font-size: 40px;
  flex-shrink: 0;
}

.company-info {
  flex: 1;
  min-width: 0;
}

.company-name {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.company-address {
  font-size: 14px;
  color: #666;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.radio-icon {
  flex-shrink: 0;
}

.radio-circle {
  width: 24px;
  height: 24px;
  border: 2px solid #ddd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.radio-circle.checked {
  border-color: #667eea;
  background: #667eea;
}

.radio-dot {
  width: 10px;
  height: 10px;
  background: #fff;
  border-radius: 50%;
}

.card-footer {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
  font-size: 14px;
  color: #666;
}

.phone-label {
  font-weight: 500;
}

.phone-value {
  color: #333;
}

.btn-confirm {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
}

.btn-confirm:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 30px rgba(102, 126, 234, 0.5);
}

.btn-confirm:active:not(.disabled) {
  transform: translateY(0);
}

.btn-confirm.disabled {
  background: #ccc;
  cursor: not-allowed;
  box-shadow: none;
}

.btn-back {
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Toast */
.toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%) translateY(-100px);
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 10000;
  transition: transform 0.3s ease;
}

.toast.show {
  transform: translateX(-50%) translateY(0);
}

.toast-success {
  background: rgba(34, 197, 94, 0.9);
}

.toast-error {
  background: rgba(239, 68, 68, 0.9);
}
</style>
